import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { displayedOpenEndedDevelopment } from '../lib/openEndedPricing';
import { mailtoHref } from '../lib/site';
import CommercialTerms from '../components/CommercialTerms';
import { useScrollEdges } from '../components/ui/useScrollEdges';
import {
  ASSESSMENT_QUESTIONS,
  DEFAULT_REQUIREMENTS,
  DEVELOPMENT_TIERS,
  HOSTING,
  HOSTING_RISK_OPTIONS,
  INTEGRATION_DEPTH_OPTIONS,
  SITE_HANDLING_OPTIONS,
  TIER_INFO,
  CONTENT_PANEL_MULTIPLIER,
  isHostingPlanAvailable,
  isLightweightAdapter,
  isTier3,
  paymentSchedule,
  requirementsForHandling,
  type IntegrationDepth,
  type ProjectRequirements,
  type SiteHandling,
  calculateEstimate,
  classifyProject,
  developmentBasis,
  eligibleForAI,
  eligibleForAdvancedPanel,
  fReviewFlags,
  formatUSD,
  getComplexityScore,
  t3ScopeReview,
  type AssessmentQuestion,
  type CategoryCode,
  type Panel,
  type Risk,
  type Scores,
} from '../lib/quoteEstimator';

const initialScores: Scores = { A: 1, B: 2, C: 1, D: 0, E: 0, F: 1 };

const stepCard =
  'panel p-4 sm:p-6';

/** Shared pill-button style for questionnaire + risk options. */
const optClass = (active: boolean) =>
  `text-left text-[13px] leading-snug px-3 py-2.5 rounded-xl border min-h-[44px] cursor-pointer transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 ${
    active
      ? 'border-primary-400/60 bg-primary-500/15 text-white font-medium shadow-[0_0_14px_rgba(14,165,233,0.18)]'
      : 'border-secondary-800 text-secondary-300 hover:border-primary-500/40 hover:text-white hover:bg-primary-500/5'
  }`;

function Tag({ children, tone = 'neutral' }: { children: React.ReactNode; tone?: 'neutral' | 'approved' | 'warn' | 'planned' }) {
  const tones: Record<string, string> = {
    neutral: 'border-secondary-700 text-secondary-400',
    approved: 'border-success-500/40 bg-success-500/10 text-success-300',
    warn: 'border-warning-500/40 bg-warning-500/10 text-warning-300',
    planned: 'border-accent-500/40 bg-accent-500/10 text-accent-300',
  };
  return (
    <span className={`ml-2 my-0.5 inline-block align-middle text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-md border whitespace-nowrap ${tones[tone]}`}>
      {children}
    </span>
  );
}

function QuestionBlock({
  q,
  value,
  onChange,
}: {
  q: AssessmentQuestion;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <fieldset>
      <legend className="text-sm font-semibold text-white mb-1">{q.title}</legend>
      <p className="text-xs text-secondary-400 mb-2">{q.hint}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" role="radiogroup" aria-label={q.title}>
        {q.options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={value === opt.value}
            onClick={() => onChange(opt.value)}
            className={optClass(value === opt.value)}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

const STEPS = [
  { id: 'project', label: 'Project' },
  { id: 'capability', label: 'Features' },
  { id: 'risk', label: 'Scale & risk' },
  { id: 'tier', label: 'Price tier' },
  { id: 'addons', label: 'Options' },
  { id: 'hosting', label: 'Hosting' },
  { id: 'estimate', label: 'Estimate' },
] as const;

/** Plain-language risk labels for the calculator (engine labels keep their F-codes). */
const PLAIN_RISK_LABELS = ['Minimal', 'Low', 'Moderate', 'Elevated', 'High', 'Critical'];

/**
 * Turn engine reason strings into plain sentences. Security reasons keep their
 * full text because the detail matters more than the shorthand.
 */
const friendlyReason = (reason: string): string => {
  const match = reason.match(/^([A-E])(\d)\/5 — (.*)$/);
  if (!match) return reason.replace(/^Capability — /, '');
  const [, code, score, detail] = match;
  const plain: Record<string, string> = {
    A: 'The design needs custom visual work.',
    B: 'There is more content than a simple one-page site.',
    C: 'Visitors need custom interactive features.',
    D: 'The site needs stored data or several integrations.',
    E: 'The launch needs high-availability or high-scale infrastructure.',
  };
  return plain[code] ? `${plain[code]} (${score}/5)` : detail;
};

function ProgressRail({ step, maxVisited, goTo }: { step: number; maxVisited: number; goTo: (i: number) => void }) {
  const listRef = useRef<HTMLOListElement>(null);
  const { start, end, scrollable } = useScrollEdges(listRef);

  // Keep the current step inside the (sideways-scrollable) rail on narrow screens.
  useEffect(() => {
    const list = listRef.current;
    const li = list?.children[step] as HTMLElement | undefined;
    if (!list || !li) return;
    const l = li.offsetLeft;
    const r = l + li.offsetWidth;
    if (l < list.scrollLeft) list.scrollLeft = Math.max(0, l - 16);
    else if (r > list.scrollLeft + list.clientWidth) list.scrollLeft = r - list.clientWidth + 16;
  }, [step]);

  return (
    <nav aria-label="Estimator progress" className="mb-4 sm:mb-5">
      <p className="sm:hidden mb-1.5 text-sm text-secondary-300" aria-hidden="true">
        Step {step + 1} of {STEPS.length} · <span className="font-semibold text-white">{STEPS[step].label}</span>
      </p>
      <div className="relative">
        <ol ref={listRef} className="flex items-center gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {STEPS.map((s, i) => {
            const current = i === step;
            const reachable = i <= maxVisited;
            const visited = reachable && !current;
            return (
              <li key={s.id} className="flex-1 min-w-[44px]">
                <button
                  type="button"
                  onClick={() => goTo(i)}
                  disabled={!reachable}
                  aria-current={current ? 'step' : undefined}
                  aria-label={`${s.label}${current ? ' (current step)' : ''}`}
                  className={`w-full flex items-center justify-center gap-1.5 px-1 sm:px-2 rounded-xl border text-xs font-semibold min-h-[44px] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 ${
                    current
                      ? 'border-primary-400/60 bg-primary-500/20 text-white'
                      : visited
                        ? 'border-secondary-700 text-secondary-200 hover:border-primary-500/40 hover:text-white cursor-pointer'
                        : 'border-secondary-800/60 text-secondary-500 cursor-not-allowed'
                  }`}
                >
                  <span
                    className={`inline-flex w-5 h-5 rounded-full text-[10px] items-center justify-center shrink-0 ${
                      current ? 'bg-primary-500/40 text-white' : visited ? 'bg-success-500/20 text-success-300' : 'bg-secondary-800 text-secondary-400'
                    }`}
                    aria-hidden="true"
                  >
                    {visited ? '✓' : i + 1}
                  </span>
                  <span className="hidden sm:inline truncate">{s.label}</span>
                </button>
              </li>
            );
          })}
        </ol>
        {scrollable && !start && <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 left-0 w-6 bg-gradient-to-r from-secondary-950 to-transparent" />}
        {scrollable && !end && <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-secondary-950 to-transparent" />}
      </div>
    </nav>
  );
}

const PricingCalculator: React.FC = () => {
  const [scores, setScores] = useState<Scores>(initialScores);
  const [panel, setPanel] = useState<Panel>('none');
  const [aiSelfHealing, setAiSelfHealing] = useState(false);
  const [fairCorpHosting, setFairCorpHosting] = useState(true);
  const [highTraffic, setHighTraffic] = useState(false);
  const [hostingRisk, setHostingRisk] = useState<Risk>(1);
  const [step, setStep] = useState(0);
  const [maxVisited, setMaxVisited] = useState(0);

  const [requirements, setRequirements] = useState<ProjectRequirements>(DEFAULT_REQUIREMENTS);
  const [unifiedAdmin, setUnifiedAdmin] = useState(false);

  const classification = useMemo(() => classifyProject(scores, requirements), [scores, requirements]);
  const complexity = useMemo(() => getComplexityScore(scores), [scores]);
  const persistentData =
    requirements.businessFunction === 'customBackendDb' ||
    requirements.backendResponsibility === 'persistent_database' ||
    requirements.needsStoredRecords;
  const basis = useMemo(() => {
    const classified = classification.tier;
    if (classified === 'T4A' || classified === 'T4B' || classified === 'T5') {
      const priced = displayedOpenEndedDevelopment(classified, scores, persistentData);
      const atFloor = priced.base === priced.floor;
      return {
        base: priced.base,
        kind: 'preliminary' as const,
        atFloor,
        note: atFloor
          ? `Approved ${classified} minimum of ${formatUSD(priced.floor)}. Answers above the reference profile raise this preliminary price. Not a binding quote.`
          : `Preliminary ${formatUSD(priced.base)} from your answers, above the approved ${formatUSD(priced.floor)} minimum. Not a binding quote.`,
      };
    }
    return { ...developmentBasis(classified, complexity), atFloor: false };
  }, [classification.tier, complexity, scores, persistentData]);
  const flags = useMemo(() => fReviewFlags(scores.F), [scores.F]);

  const tier = classification.tier;
  const advancedEligible = eligibleForAdvancedPanel(tier);
  const aiEligible = eligibleForAI(tier);

  // Guard: tier changes can invalidate panel/AI selections — coerce safely.
  const effectivePanel: Panel = panel === 'advanced' && !advancedEligible ? 'none' : panel;
  const effectiveAI = aiSelfHealing && aiEligible && fairCorpHosting ? true : false;
  // Tier 3 capabilities: both recorded => ONE commission (never stacked); scope review, never T4.
  const t3Both = !!classification.tier3 && classification.tier3.presentation && classification.tier3.integration;
  // Administering orders / third-party account data from the panel needs an integration/backend assessment.
  const panelAssessment = unifiedAdmin && panel !== 'none';

  const estimate = useMemo(
    () =>
      calculateEstimate({
        tier,
        baseDevelopmentPrice: basis.base,
        panel: effectivePanel,
        aiSelfHealing: effectiveAI,
        fairCorpHosting,
        highTraffic,
        hostingRisk,
        t3Both: t3Both,
        unifiedAdminPanel: panelAssessment,
        lightweightAdapter: tier === 'T3B' && isLightweightAdapter(requirements),
      }),
    [tier, basis.base, effectivePanel, effectiveAI, fairCorpHosting, highTraffic, hostingRisk, t3Both, panelAssessment, requirements],
  );

  const tierInfo = TIER_INFO.find((t) => t.tier === tier)!;
  const hostingAvailable = !estimate.hostingPlan || isHostingPlanAvailable(estimate.hostingPlan);
  const monthlyEstimate = hostingAvailable ? estimate.monthlyEstimate : null;
  const needsReview =
    estimate.requiresReview || flags.securityReview || flags.t5EligibilityAssessment;
  const scopeReview = t3ScopeReview(tier, basis.base, t3Both);
  const thirdPartyCosts = requirements.businessFunction === 'thirdParty' || isLightweightAdapter(requirements);
  const handling: SiteHandling =
    requirements.businessFunction === 'customBackendDb' || requirements.backendResponsibility === 'persistent_database'
      ? 'database'
      : requirements.businessFunction === 'customBackend' || requirements.backendResponsibility === 'custom_processing'
        ? 'processing'
        : isLightweightAdapter(requirements)
          ? 'adapter'
          : requirements.businessFunction === 'thirdParty'
            ? 'links'
            : 'informational';
  const setReq = (patch: Partial<ProjectRequirements>) => setRequirements((r) => ({ ...r, ...patch }));

  const reset = () => {
    setScores(initialScores);
    setRequirements(DEFAULT_REQUIREMENTS);
    setUnifiedAdmin(false);
    setPanel('none');
    setAiSelfHealing(false);
    setFairCorpHosting(true);
    setHighTraffic(false);
    setHostingRisk(1);
    setStep(0);
    setMaxVisited(0);
  };

  const goTo = (i: number) => {
    if (i < 0 || i >= STEPS.length || i > maxVisited) return;
    setStep(i);
  };
  const next = () => {
    const n = Math.min(step + 1, STEPS.length - 1);
    setStep(n);
    setMaxVisited((m) => Math.max(m, n));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const setScore = (code: CategoryCode, value: number) =>
    setScores((s) => ({ ...s, [code]: value }));

  const question = (code: CategoryCode) => ASSESSMENT_QUESTIONS.find((q) => q.code === code)!;

  // Prefilled inquiry bodies: development and hosting stay on separate lines (separate contracts).
  const shownUpfront = estimate.upfrontEstimate - (effectiveAI ? estimate.aiCost : 0);
  const devLines = [
    `Development estimate (one-time): ${formatUSD(shownUpfront)} — ${tierInfo.name}`,
    `Score ${complexity}/30 · management interface: ${effectivePanel === 'none' ? 'none' : effectivePanel}${effectiveAI ? ' · AI self-healing requested (completion requisite, official engineering quote — not in the figure above)' : ''}`,
    `Payment: ${paymentSchedule(tier)}`,
  ];
  const hostLines = fairCorpHosting
    ? hostingAvailable
      ? [
          `Hosting estimate (monthly, optional): ${estimate.hostingPlan} — ${monthlyEstimate === null ? 'custom assessment' : formatUSD(monthlyEstimate) + '/mo'}`,
          `Hosting security level (illustrative): ${PLAIN_RISK_LABELS[hostingRisk]}`,
        ]
      : [
          `Hosting request: ${estimate.hostingPlan} — H5 hosting is not available at this time; no monthly price is quoted.`,
        ]
    : [];
  const noteLines = ['', 'Informational estimates from the quote tool — not contracts.', '', 'About my project:', ''];
  const developmentOnlyInquiry = mailtoHref('development', ['Development inquiry', '', ...devLines, ...noteLines]);
  const primaryInquiry = fairCorpHosting
    ? mailtoHref('both', ['Development + hosting inquiry', '', ...devLines, '', ...hostLines, ...noteLines])
    : developmentOnlyInquiry;

  return (
    <section aria-labelledby="calculator-title" className="animate-fade-in">
        {/* Header */}
        <div className="panel p-4 sm:p-5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="chip-dev">One-time development</span>
            <span className="text-xs font-semibold px-2.5 py-1 rounded-full border border-primary-500/40 bg-primary-500/10 text-primary-200">
              Preview only — not a contractual quote
            </span>
          </div>
          <h2 id="calculator-title" className="mt-3 text-xl sm:text-2xl font-bold tracking-tight text-white">Find your price tier</h2>
          <p className="mt-2 text-sm text-secondary-300">
            Answer seven short questions in everyday language. You will see the tier your project fits and a realistic one-time price.{' '}
            <span className="text-white font-semibold">Nothing is submitted, saved, or tied to you.</span>
          </p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2">
            <Link to="/tiers" className="inline-flex items-center min-h-[44px] text-sm text-primary-300 hover:text-white underline underline-offset-4">
              View all tier details
            </Link>
            <Link to="/plans" className="inline-flex items-center min-h-[44px] text-sm text-accent-200 hover:text-white underline underline-offset-4">
              Hosting only? See plans
            </Link>
          </div>
          <div className="mt-3 space-y-2 border-t border-secondary-800 pt-3">
            <p className="text-sm text-secondary-300">
              <span className="font-semibold text-white">Already have the code?</span> If you own substantial source code, development is 50% of the listed tier price. If the code is lost and your site is still online, we can rebuild it from a webscrape at 70% of the listed tier price.
            </p>
            <p className="text-sm text-secondary-400">
              These are planning estimates, not a contract. The final quote is confirmed after a short scope review. Projects in Tier 4 and above always include an engineering review.
            </p>
          </div>
        </div>

        <div className="mt-4">
        {/* Progress rail */}
        <ProgressRail step={step} maxVisited={maxVisited} goTo={goTo} />

        {/* Step body */}
        <div key={step} className="animate-fade-in">
          {step === 0 && (
            <div className={stepCard}>
              <h2 className="text-xl font-bold tracking-tight text-white mb-1">What are you building?</h2>
              <p className="text-sm text-secondary-400 mb-4">Start with the look and the amount of content. Pick the closest answer — you can change it later.</p>
              <div className="space-y-5">
                <QuestionBlock q={question('A')} value={scores.A} onChange={(v) => setScore('A', v)} />
                <QuestionBlock q={question('B')} value={scores.B} onChange={(v) => setScore('B', v)} />
              </div>
            </div>
          )}

          {step === 1 && (
            <div className={stepCard}>
              <h2 className="text-xl font-bold tracking-tight text-white mb-1">What should it do?</h2>
              <p className="text-sm text-secondary-400 mb-4">Tell us how the site handles orders, bookings, data, or other business tasks.</p>
              <div className="space-y-5">
                <fieldset>
                  <legend className="text-sm font-semibold text-white mb-1">How should your website handle business functionality?</legend>
                  <p className="text-xs text-secondary-400 mb-2">
                    T3B — Integrated Services. Existing services remain responsible for core business processing.
                  </p>
                  <div className="grid grid-cols-1 gap-2" role="radiogroup" aria-label="Business functionality">
                    {SITE_HANDLING_OPTIONS.map((o) => (
                      <button
                        key={o.value}
                        type="button"
                        role="radio"
                        aria-checked={handling === o.value}
                        onClick={() => setRequirements(requirementsForHandling(o.value, requirements))}
                        className={optClass(handling === o.value)}
                      >
                        <span className="block font-semibold">{o.label}</span>
                        <span className="block text-secondary-400 mt-0.5">{o.detail}</span>
                      </button>
                    ))}
                  </div>
                  {handling === 'links' && (
                    <div className="mt-3 rounded-xl border border-secondary-800 p-3">
                      <p className="text-xs font-semibold text-secondary-300 mb-2">How deeply do you need to connect those services?</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" role="radiogroup" aria-label="Integration depth">
                        {INTEGRATION_DEPTH_OPTIONS.map((o) => (
                          <button
                            key={o.value}
                            type="button"
                            role="radio"
                            aria-checked={requirements.integrationDepth === o.value}
                            onClick={() => setReq({ integrationDepth: o.value as IntegrationDepth })}
                            className={optClass(requirements.integrationDepth === o.value)}
                          >
                            <span className="block font-semibold">{o.label}</span>
                            <span className="block text-secondary-400 mt-0.5">{o.detail}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-secondary-400 mt-2">
                    Simple links or embeds may stay in the single-page or multi-page tier. A small connection to a service you already use is the third-party integrations tier. Building new business processing is a custom backend project; storing that data is a database-backed application. Higher traffic, infrastructure, or security needs can move a project up a tier.
                  </p>
                </fieldset>
                <QuestionBlock q={question('C')} value={scores.C} onChange={(v) => setScore('C', v)} />
                <QuestionBlock q={question('D')} value={scores.D} onChange={(v) => setScore('D', v)} />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className={stepCard}>
              <h2 className="text-xl font-bold tracking-tight text-white mb-1">How much traffic and risk?</h2>
              <p className="text-sm text-secondary-400 mb-4">
                Traffic is about how hard the site works. Risk is about the information it holds and what could go wrong. Answer honestly — this only sets the right review level.
              </p>
              <div className="space-y-5">
                <QuestionBlock q={question('E')} value={scores.E} onChange={(v) => setScore('E', v)} />
                <QuestionBlock q={question('F')} value={scores.F} onChange={(v) => setScore('F', v)} />
              </div>
              <div className="flex items-center gap-3 mt-5 pt-4 border-t border-secondary-800/60 text-xs text-secondary-400">
                <span className="font-semibold text-secondary-300">Project complexity</span>
                <span className="h-1.5 flex-1 rounded-full bg-secondary-800 overflow-hidden" aria-hidden="true">
                  <span className="block h-full rounded-full bg-primary-400" style={{ width: `${Math.round((complexity / 30) * 100)}%` }} />
                </span>
                <span className="font-semibold text-white" data-testid="complexity-score">{complexity}/30</span>
              </div>
            </div>
          )}

          {step === 3 && (
            <>
            <div className="rounded-2xl p-4 sm:p-6 border border-primary-500/25 bg-gradient-to-r from-primary-900/40 to-accent-900/40">
              <h2 className="text-xl font-bold tracking-tight text-white mb-1">Your price tier</h2>
              <p className="text-xs text-secondary-400">Complexity: {complexity}/30 · Go back to adjust answers any time.</p>
              <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mt-3">
                <span className="text-2xl font-bold text-white">{tierInfo.name}</span>
                <span className="text-lg text-primary-300 font-semibold">
                  {formatUSD(basis.base)}{basis.kind === 'starting' ? ' starting price' : ' estimated price'}
                </span>
              </div>
              <p className="text-sm text-secondary-300 mt-2">{tierInfo.tagline}</p>
              <ul className="mt-3 space-y-1.5">
                {classification.reasons.map((r, i) => (
                  <li key={i} className="text-sm text-secondary-300 flex gap-2">
                    <span className="text-primary-400" aria-hidden="true">▸</span>
                    <span>{friendlyReason(r)}</span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-secondary-400 mt-3 leading-relaxed">
                {basis.kind === 'preliminary' || basis.kind === 'starting'
                  ? `${tier} projects start at ${formatUSD(DEVELOPMENT_TIERS[tier].min)}. The final figure is confirmed after an engineering review of the scope.`
                  : `This sits inside the approved ${tier} range of ${formatUSD(DEVELOPMENT_TIERS[tier].min)}–${formatUSD(DEVELOPMENT_TIERS[tier].max as number)}. The final figure is confirmed after a short scope review.`}
              </p>
              <Link
                to={`/tiers/${tier}`}
                className="mt-3 inline-flex items-center min-h-[44px] text-sm font-semibold text-primary-300 hover:text-white underline underline-offset-4"
              >
                See what is included in {tier}
              </Link>
              {flags.securityReview && (
                <p className="mt-2 text-xs text-warning-300 bg-warning-500/10 border border-warning-500/30 rounded-xl px-3 py-2.5">
                  {scores.F >= 5
                    ? `F5/5 — critical exposure. T5 or enterprise commission only, with explicit security, legal, and operational acceptance before quoting. FairCorp may require specialist controls, insurance, external expertise — or decline the commission.`
                    : flags.t5EligibilityAssessment
                      ? `F${scores.F}/5 — high exposure triggers a mandatory T5 assessment (does not force T5). Security-engineering effort, testing, and contingency are scoped at review — never a fixed surcharge.`
                      : `F${scores.F}/5 — elevated exposure: additional safeguards and security testing must be scoped into the development estimate, plus a security review.`}
                </p>
              )}
            </div>
            <div className="mt-3 space-y-2" data-testid="classification-notes">
              {classification.tier3 && (
                <p className="text-xs text-secondary-300 bg-secondary-900/60 border border-secondary-800 rounded-xl px-3 py-2.5">
                  {classification.tier3.presentation && classification.tier3.integration
                    ? 'Recorded: Tier 3A (presentation) + Tier 3B (integration) — one Tier 3 commission, $1,500–$5,000.'
                    : classification.tier3.integration
                      ? 'Tier 3B — we connect existing services. The same approved $1,500–$5,000 range as Tier 3A; the difference is where the effort goes, not quality.'
                      : 'Tier 3A — we build the experience. The same approved $1,500–$5,000 range as Tier 3B; the difference is where the effort goes, not quality.'}
                </p>
              )}
              {classification.tier3?.integration && (
                <p className="text-xs text-secondary-400 px-1">
                  Third-party providers remain responsible for their own services under their own contracts. FairCorp is responsible for the integration work it commissions and its own parts of the site.
                </p>
              )}
              {scopeReview && (
                <p className="text-xs text-warning-300 bg-warning-500/10 border border-warning-500/30 rounded-xl px-3 py-2.5">
                  Scope review: the combined presentation and integration scope sits at the top of the Tier 3 range and may not fit within $5,000. FairCorp reviews that scope with you — it is not reclassified to Tier 4.
                </p>
              )}
              {(classification.notes ?? [])
                .filter((n) => !n.startsWith('Both Tier 3A'))
                .map((n) => (
                  <p key={n} className="text-xs text-warning-300 bg-warning-500/10 border border-warning-500/30 rounded-xl px-3 py-2.5">{n}</p>
                ))}
            </div>
            </>
          )}

          {step === 4 && (
            <div className={stepCard}>
              <h2 className="text-xl font-bold tracking-tight text-white mb-1">Optional extras for your {tier} project</h2>
              <p className="text-sm text-secondary-400 mb-4">
                Choose how you want to update the site after launch. Skipping this is fine — you can ask FairCorp for changes instead.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6" role="radiogroup" aria-label="Management interface">
                {(
                  [
                    { v: 'none' as Panel, t: 'No editor', d: 'You edit files directly or ask FairCorp.', cost: '+$0' },
                    { v: 'content' as Panel, t: 'Content editor', d: 'Edit text, menus, prices, and images yourself.', cost: '+' + formatUSD(basis.base * CONTENT_PANEL_MULTIPLIER) },
                    { v: 'advanced' as Panel, t: 'Admin console', d: advancedEligible ? 'Manage records, accounts, permissions, and orders.' : 'Available on Tier 4 and above. If you need it, we will re-scope the project.', cost: advancedEligible ? '+' + formatUSD(basis.base) + ' minimum' : 'Not at this tier' },
                  ]
                ).map((o) => {
                  const disabled = o.v === 'advanced' && !advancedEligible;
                  const active = effectivePanel === o.v;
                  return (
                    <button
                      key={o.v}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      disabled={disabled}
                      onClick={() => setPanel(o.v)}
                      className={`text-left p-4 rounded-xl border min-h-[64px] cursor-pointer transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 ${
                        active
                          ? 'border-primary-400/60 bg-primary-500/15 shadow-[0_0_14px_rgba(14,165,233,0.18)]'
                          : disabled
                            ? 'border-secondary-800/60 opacity-50 cursor-not-allowed'
                            : 'border-secondary-800 hover:border-primary-500/40 hover:bg-primary-500/5'
                      }`}
                    >
                      <div className="text-sm font-semibold text-white">{o.t}</div>
                      <div className="text-xs text-secondary-400 mt-1">{o.d}</div>
                      <div className="text-sm font-bold text-primary-300 mt-2">{o.cost}</div>
                    </button>
                  );
                })}
              </div>
              {effectivePanel !== 'none' && (
                <div className="mb-4">
                  <label className="flex items-start gap-3 p-3 rounded-xl border border-secondary-700 cursor-pointer hover:border-primary-500/40">
                    <input
                      type="checkbox"
                      className="mt-0.5 w-5 h-5 accent-sky-500 cursor-pointer"
                      checked={unifiedAdmin}
                      onChange={(e) => setUnifiedAdmin(e.target.checked)}
                    />
                    <span className="text-xs text-secondary-300">
                      I also want to administer customer orders or third-party account data from this panel.
                    </span>
                  </label>
                  {panelAssessment && (
                    <p className="mt-2 text-xs text-warning-300 bg-warning-500/10 border border-warning-500/30 rounded-xl px-3 py-2.5">
                      {effectivePanel === 'content'
                        ? 'The 0.75× standard panel covers FairCorp-site content only. Managing orders or third-party account data needs an additional integration/backend assessment, so no panel price is applied automatically.'
                        : 'Managing orders or third-party account data from one panel needs an additional integration/backend assessment on top of the 1.00× minimum.'}
                    </p>
                  )}
                </div>
              )}
              {tier === 'T4A' && effectivePanel === 'advanced' && (
                <p className="mb-4 text-xs text-warning-300 bg-warning-500/10 border border-warning-500/30 rounded-xl px-3 py-2.5">
                  Scope review: a T4A advanced panel needing persistent application records may be re-scoped as T4B before any contract.
                </p>
              )}
              {panel === 'advanced' && !advancedEligible && (
                <p className="mb-4 text-xs text-secondary-400 border border-secondary-800 rounded-xl px-3 py-2.5">
                  Advanced was cleared — it needs T4+ and your project classifies as {tier}.
                </p>
              )}

              <div className="flex flex-wrap items-center gap-2 mt-2 mb-1">
                <h3 className="text-base font-bold text-white">Automated self-repair</h3>
                <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-accent-500/15 border border-accent-500/40 text-accent-300">
                  ENGINEERING QUOTE
                </span>
              </div>
              <p className="text-xs text-secondary-400 mb-3">
                Sometimes called AI self-healing. If you request it, the build includes it in full. The price is set by an official engineering quote — it is not a self-serve add-on, and it is not available below Tier 4.
              </p>
              <label
                className={`flex items-start gap-3 p-4 rounded-xl border transition-colors duration-200 cursor-pointer ${
                  aiEligible && fairCorpHosting
                    ? 'border-secondary-700 hover:border-primary-500/40 hover:bg-primary-500/5'
                    : 'border-secondary-800/60 opacity-60 cursor-not-allowed'
                }`}
              >
                <input
                  type="checkbox"
                  className="mt-1 w-5 h-5 accent-sky-500 cursor-pointer"
                  checked={effectiveAI}
                  disabled={!aiEligible || !fairCorpHosting}
                  onChange={(e) => setAiSelfHealing(e.target.checked)}
                />
                <span>
                  <span className="block text-sm font-semibold text-white">Enable automated self-repair (AI self-healing)</span>
                  <span className="block text-xs text-secondary-400 mt-1">
                    {!aiEligible
                      ? `Not available at ${tier} — it starts at Tier 4 (T4A, T4B, or T5).`
                      : !fairCorpHosting
                        ? 'Requires FairCorp hosting — turn hosting on in the next step to unlock.'
                        : 'The build includes it in full. The price is an official engineering quote, not a self-serve amount.'}
                  </span>
                </span>
              </label>
            </div>
          )}

          {step === 5 && (
            <div className={stepCard}>
              <h2 className="text-xl font-bold tracking-tight text-white mb-1">Do you need hosting too?</h2>
              <p className="text-sm text-secondary-400 mb-4">
                Hosting is optional and billed monthly on its own contract. Turn it off for a development-only estimate. If you keep it on, choose the security and liability level that fits the site.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                <label className="flex items-center gap-3 p-4 rounded-xl border border-secondary-700 cursor-pointer transition-colors duration-200 hover:border-primary-500/40 hover:bg-primary-500/5">
                  <input
                    type="checkbox"
                    className="w-5 h-5 accent-sky-500 cursor-pointer"
                    checked={fairCorpHosting}
                    onChange={(e) => setFairCorpHosting(e.target.checked)}
                  />
                  <span className="text-sm font-semibold text-white">FairCorp hosting (optional, monthly)</span>
                </label>
                <label className={`flex items-center gap-3 p-4 rounded-xl border transition-colors duration-200 ${fairCorpHosting ? 'border-secondary-700 cursor-pointer hover:border-primary-500/40 hover:bg-primary-500/5' : 'border-secondary-800/60 opacity-50'}`}>
                  <input
                    type="checkbox"
                    className="w-5 h-5 accent-sky-500 cursor-pointer"
                    checked={highTraffic}
                    disabled={!fairCorpHosting}
                    onChange={(e) => setHighTraffic(e.target.checked)}
                  />
                  <span>
                    <span className="block text-sm font-semibold text-white">High-traffic site</span>
                    <span className="block text-[11px] text-secondary-400">Self-declared — official traffic thresholds not yet defined.</span>
                  </span>
                </label>
              </div>
              {fairCorpHosting && isTier3(tier) && (
                <p className="text-xs text-secondary-400 border border-secondary-800 rounded-xl px-3 py-2.5 mb-3">
                  {tier === 'T3B' ? 'T3B' : 'T3A'} is a <span className="text-white font-semibold">development</span> tier; H3a and H3b are{' '}
                  <span className="text-white font-semibold">hosting</span> plans. Matching letters imply nothing — hosting is chosen from what your site needs, not from how it was built.
                  {isLightweightAdapter(requirements)
                    ? ' A small FairCorp-hosted integration uses H3b ($50/mo). Provider API fees stay your responsibility unless separately agreed.'
                    : ' A provider-hosted or frontend-only Tier 3 site uses H3a ($35/mo) unless you add a content editor or high traffic.'}
                </p>
              )}
              {fairCorpHosting && (
                hostingAvailable ? (
                  <>
                    <p className="text-xs text-secondary-400 mb-2">
                      Suggested hosting plan: <span className="text-white font-semibold">{estimate.hostingPlan}</span>
                      {' '}· {formatUSD(estimate.baseMonthly)}/mo base
                      <span className="text-secondary-400"> (based on tier, editor, traffic, and self-repair)</span>
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" role="radiogroup" aria-label="Security and liability level">
                      {HOSTING_RISK_OPTIONS.map((r) => (
                        <button
                          key={r.value}
                          type="button"
                          role="radio"
                          aria-checked={hostingRisk === r.value}
                          onClick={() => setHostingRisk(r.value)}
                          className={optClass(hostingRisk === r.value)}
                        >
                          <span className="block font-semibold">{PLAIN_RISK_LABELS[r.value]}</span>
                          <span className="block text-secondary-400 mt-0.5">
                            {r.value <= 1
                              ? 'No extra allowance · standard review'
                              : r.value === 2
                                ? 'Adds 5% · standard review'
                                : r.value === 3
                                  ? 'Adds 15% · security review'
                                  : r.value === 4
                                    ? 'Adds 30% · custom security review'
                                    : 'Custom quote · manual assessment required'}
                          </span>
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-secondary-400 mt-2">
                      These adjustments are illustrative and <span className="text-warning-300 font-semibold">not approved FairCorp policy</span>. We confirm the final monthly price with you.
                    </p>
                  </>
                ) : (
                  <div role="status" className="rounded-xl border border-warning-500/40 bg-warning-500/5 px-3 py-3">
                    <p className="text-xs text-secondary-400">
                      Suggested hosting plan: <span className="text-white font-semibold">{estimate.hostingPlan}</span>
                      {' '}· <span className="font-semibold text-warning-300">Not available at this time</span>
                    </p>
                    <p className="mt-2 text-sm text-secondary-300">
                      This project falls into the H5 hosting family. All H5 plans are approved but not currently offered, so no monthly price or security-allowance estimate is shown.
                    </p>
                    <p className="mt-1 text-xs text-secondary-400">
                      You can continue to record hosting interest in the inquiry. The development estimate is unaffected.
                    </p>
                  </div>
                )
              )}
            </div>
          )}

          {step === 6 && (
            <div className="space-y-4">
              <div className="panel p-4 sm:p-6">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h2 className="text-xl font-bold tracking-tight text-white">Your estimated price</h2>
                  <button
                    type="button"
                    onClick={reset}
                    className="btn btn-quiet text-xs px-4"
                  >
                    Start over
                  </button>
                </div>
                <p className="mt-1 text-xs text-secondary-400">
                  {tier} · complexity {complexity}/30{fairCorpHosting && hostingAvailable ? ` · ${PLAIN_RISK_LABELS[hostingRisk]} security level` : ''} · preview only
                </p>

                <section aria-labelledby="est-dev" className="mt-4 rounded-xl border border-primary-400/30 bg-primary-500/5 p-3 sm:p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h3 id="est-dev" className="text-sm font-bold text-white">Development</h3>
                    <span className="chip-dev">One-time · its own contract</span>
                  </div>
                  <dl className="mt-3 space-y-2.5 text-sm">
                    <div className="flex justify-between items-start gap-3 sm:gap-4">
                      <dt className="text-secondary-300 min-w-0 leading-relaxed">
                        {basis.kind === 'starting' ? 'Base development' : 'Estimated development'} ({tier})
                        <Tag tone="approved">
                          {basis.kind === 'preliminary'
                            ? (basis.atFloor ? 'Approved minimum' : 'From your answers')
                            : basis.kind === 'starting'
                              ? 'Approved minimum'
                              : 'Within the approved range'}
                        </Tag>
                        {basis.kind === 'interpolated' && <Tag tone="warn">Estimate</Tag>}
                      </dt>
                      <dd className="text-white font-semibold shrink-0 text-right">{formatUSD(estimate.development)}</dd>
                    </div>
                    {effectivePanel !== 'none' && !(panelAssessment && effectivePanel === 'content') && (
                      <div className="flex justify-between items-start gap-3 sm:gap-4">
                        <dt className="text-secondary-300 min-w-0 leading-relaxed">
                          {effectivePanel === 'content' ? 'Content editor (75% of the build)' : 'Admin console (100% of the build minimum)'}
                          <Tag tone="approved">Approved add-on</Tag>
                        </dt>
                        <dd className="text-white font-semibold shrink-0 text-right">{formatUSD(estimate.panelCost)}</dd>
                      </div>
                    )}
                    {panelAssessment && effectivePanel === 'content' && (
                      <div className="flex justify-between items-start gap-3 sm:gap-4">
                        <dt className="text-secondary-300 min-w-0 leading-relaxed">
                          Management of orders / third-party account data
                          <Tag tone="warn">Assessment required</Tag>
                        </dt>
                        <dd className="text-warning-300 font-semibold shrink-0 text-right">To be assessed</dd>
                      </div>
                    )}
                    {effectiveAI && (
                      <div className="flex justify-between items-start gap-3 sm:gap-4">
                        <dt className="text-secondary-300 min-w-0 leading-relaxed">Automated self-repair (AI self-healing)
                          <Tag tone="warn">Engineering quote</Tag>
                        </dt>
                        <dd className="text-warning-300 font-semibold shrink-0 text-right">Official quote</dd>
                      </div>
                    )}
                  </dl>
                  <div className="mt-3 pt-3 border-t border-primary-400/20">
                    <p className="text-xs text-secondary-400 uppercase tracking-wide">Estimated one-time development</p>
                    <p className="text-3xl font-bold text-white">{formatUSD(shownUpfront)}</p>
                  </div>
                </section>

                <section aria-labelledby="est-host" className="mt-3 rounded-xl border border-accent-400/30 bg-accent-500/5 p-3 sm:p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h3 id="est-host" className="text-sm font-bold text-white">Hosting</h3>
                    <span className="chip-host">Optional · monthly · separate contract</span>
                  </div>
                  <dl className="mt-3 space-y-2.5 text-sm">
                    <div className="flex justify-between items-start gap-3 sm:gap-4">
                      <dt className="text-secondary-300 min-w-0 leading-relaxed">
                        {fairCorpHosting ? `Suggested hosting · ${estimate.hostingPlan}` : 'FairCorp hosting — not selected'}
                        {fairCorpHosting && (hostingAvailable
                          ? <Tag tone="approved">Approved plan price</Tag>
                          : <Tag tone="warn">Not available at this time</Tag>)}
                      </dt>
                      <dd className={`font-semibold shrink-0 text-right ${hostingAvailable ? 'text-white' : 'text-warning-300'}`}>
                        {!fairCorpHosting ? '$0' : hostingAvailable ? `${formatUSD(estimate.baseMonthly)}/mo` : 'Not offered'}
                      </dd>
                    </div>
                    {fairCorpHosting && hostingAvailable && estimate.monthlyEstimate !== null && (
                      <div className="flex justify-between items-start gap-3 sm:gap-4">
                        <dt className="text-secondary-300 min-w-0 leading-relaxed">{PLAIN_RISK_LABELS[hostingRisk]} security allowance
                          <Tag tone="warn">Illustrative — not policy</Tag>
                        </dt>
                        <dd className="text-white font-semibold shrink-0 text-right">
                          {estimate.monthlyRiskAllowance === 0 ? '$0/mo' : `+${formatUSD(estimate.monthlyRiskAllowance ?? 0)}/mo`}
                        </dd>
                      </div>
                    )}
                  </dl>
                  <div className="mt-3 pt-3 border-t border-accent-400/20">
                    <p className="text-xs text-secondary-400 uppercase tracking-wide">Estimated monthly</p>
                    {fairCorpHosting && !hostingAvailable ? (
                      <p className="text-lg font-bold text-warning-300">H5 not available at this time</p>
                    ) : monthlyEstimate === null ? (
                      <p className="text-lg font-bold text-warning-300">Custom assessment required</p>
                    ) : (
                      <p className="text-3xl font-bold text-white">
                        {formatUSD(monthlyEstimate ?? 0)}
                        <span className="text-base font-medium text-secondary-400">/mo</span>
                      </p>
                    )}
                  </div>
                  <div className="mt-3">
                    {fairCorpHosting ? (
                      <button type="button" onClick={() => setFairCorpHosting(false)} className="btn btn-quiet text-xs px-4">
                        Development only — remove hosting
                      </button>
                    ) : (
                      <button type="button" onClick={() => setFairCorpHosting(true)} className="btn btn-quiet text-xs px-4">
                        Add optional hosting
                      </button>
                    )}
                  </div>
                </section>

                {fairCorpHosting && hostingAvailable && estimate.monthlyEstimate === null && (
                  <p className="mt-3 text-xs text-warning-300 bg-warning-500/10 border border-warning-500/30 rounded-xl px-3 py-2.5">
                    A critical security level needs a custom assessment, so we do not show an automatic monthly price.
                  </p>
                )}
                {needsReview && estimate.monthlyEstimate !== null && (
                  <p className="mt-3 text-xs text-warning-300 bg-warning-500/10 border border-warning-500/30 rounded-xl px-3 py-2.5">
                    Requires review before contract:{' '}
                    {[
                      flags.t5EligibilityAssessment ? 'a high-risk security review' : null,
                      flags.securityReview && !flags.t5EligibilityAssessment ? 'a security review' : null,
                      hostingRisk >= 4 ? 'elevated hosting risk' : null,
                      tier === 'T4A' && effectivePanel === 'advanced' ? 'an admin-console scope review' : null,
                      scopeReview ? 'a combined T3A + T3B scope review' : null,
                      panelAssessment ? 'an assessment for order or third-party data administration' : null,
                      classification.incompatible && requirements.businessFunction !== 'informational' ? 'answers that route to a higher tier' : null,
                      basis.kind === 'preliminary' ? 'an engineering quote for the final Tier 4+ figure' : basis.kind === 'starting' ? 'an engineering quote' : null,
                    ].filter(Boolean).join(' · ')}
                    .
                  </p>
                )}
                {thirdPartyCosts && (
                  <p className="mt-3 text-xs text-secondary-300 bg-secondary-900/60 border border-secondary-800 rounded-xl px-3 py-2.5">
                    Third-party costs — to be confirmed: provider subscriptions, transaction charges, and other service fees are separate from FairCorp&apos;s development and hosting prices and are not included above.
                  </p>
                )}
                <p className="text-xs text-secondary-400 mt-4 leading-relaxed">
                  Preliminary estimates only — not binding. The final price is confirmed after a short scope review.
                  Some thresholds and risk percentages shown here are illustrative placeholders, not approved policy.
                  An admin console may cost more than its 100% minimum. Automated self-repair, if requested, is included
                  in the build and priced by an official engineering quote — not a self-serve line. Hosting and development
                  are independent contracts.
                </p>
              </div>

              <CommercialTerms tier={tier} compact id="estimate-commercial-terms" />

              <div className="panel p-4 sm:p-6">
                <h2 className="text-lg font-bold tracking-tight text-white">Like your estimate?</h2>
                <p className="mt-1 text-sm text-secondary-300">
                  This tool is read-only. Email the numbers as an inquiry and staff will review scope with you.
                  {fairCorpHosting ? ' Development and hosting go in one message but stay separate contracts.' : ''}
                </p>
                <div className="mt-4 flex flex-col sm:flex-row gap-3">
                  <a href={primaryInquiry} className="btn btn-primary">
                    {fairCorpHosting ? 'Email a combined inquiry' : 'Email a development inquiry'}
                  </a>
                  {fairCorpHosting ? (
                    <a href={developmentOnlyInquiry} className="btn btn-outline">
                      Development-only inquiry
                    </a>
                  ) : (
                    <Link to="/plans" className="btn btn-outline">
                      Hosting plans
                    </Link>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Wizard nav — sticks to the bottom of the viewport on phones so Next is always reachable */}
        {step < STEPS.length - 1 && (
          <div className="sticky bottom-0 z-20 -mx-4 sm:mx-0 mt-5 px-4 sm:px-0 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:pb-0 bg-secondary-950/95 backdrop-blur border-t border-secondary-800 sm:border-0 sm:bg-transparent sm:backdrop-blur-none flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={back}
              disabled={step === 0}
              className="btn btn-quiet px-5 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              ← Back
            </button>
            <button type="button" onClick={next} className="btn btn-primary px-6 flex-1 sm:flex-none">
              {step === STEPS.length - 2 ? 'See my estimate →' : 'Next →'}
            </button>
          </div>
        )}
        {step === STEPS.length - 1 && (
          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => goTo(0)}
              className="text-primary-400 hover:text-primary-300 underline underline-offset-4 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-secondary-950 rounded min-h-[44px] px-4"
            >
              ← Go back and adjust answers
            </button>
          </div>
        )}
        </div>
    </section>
  );
};

export default PricingCalculator;
